import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { collection, deleteField, doc, getDocs, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { UI } from './settingsUi';
import { AUTO_REPLY_LANGS } from '../../utils/whatsappAutoReplyFlows';
import type { AutoReplyLang, LangText } from '../../utils/whatsappAutoReplyFlows';
import { TEMPLATE_DRAFTS, TEMPLATE_LANG_CODES, templateComponents } from '../../utils/whatsappTemplateDrafts';
import type { TemplateDraft } from '../../utils/whatsappTemplateDrafts';

export interface TemplateStatus {
  status: string;
  category?: string;
  error?: string | null;
}

export interface MetaTemplateRow {
  id: string;
  name: string;
  language: string;
  status: string;
  category: string;
}

/** Saved edits of a built-in template (whatsappTemplateEdits/{base}). */
export interface TemplateEditDoc {
  draftRevision?: number;
  body?: Partial<LangText>;
  footer?: Partial<LangText>;
  buttons?: Array<Partial<LangText>>;
  /** Per language: name automations send now (live) and the version in review (next). */
  names?: Partial<Record<AutoReplyLang, { live?: string | null; next?: string | null }>>;
  versionCounter?: number;
  /** What was last submitted per language, to tell "edited since submit". */
  submitted?: Partial<Record<AutoReplyLang, { name: string; hash: string }>>;
}

export const templateKey = (name: string, lang: string) => `${name}__${lang}`;

const EDITS_COLLECTION = 'whatsappTemplateEdits';
const IN_REVIEW = new Set(['PENDING', 'IN_APPEAL']);
const FIXABLE = new Set(['REJECTED']);

const pickLang = (text: Partial<LangText> | undefined, base: LangText, lang: AutoReplyLang) =>
  typeof text?.[lang] === 'string' && text[lang]!.trim() ? String(text[lang]) : base[lang];

/** Built-in draft with the saved edits applied. */
export const applyEdits = (draft: TemplateDraft, edit?: TemplateEditDoc): TemplateDraft => {
  if (!edit) return draft;
  if ((draft.draftRevision || 0) > (edit.draftRevision || 0)) return draft;
  const merge = (base: LangText, over?: Partial<LangText>): LangText => ({
    en: pickLang(over, base, 'en'),
    gu: pickLang(over, base, 'gu'),
    hi: pickLang(over, base, 'hi'),
  });
  return {
    ...draft,
    body: merge(draft.body, edit.body),
    footer: draft.footer ? merge(draft.footer, edit.footer) : undefined,
    buttons: draft.buttons?.map((b, i) => merge(b, edit.buttons?.[i])),
  };
};

/** True when a saved edit changes any text (empty strings mean "use the original"). */
export const hasEdits = (edit?: TemplateEditDoc) => {
  if (!edit) return false;
  const filled = (t?: Partial<LangText>) => !!t && Object.values(t).some((v) => String(v || '').trim());
  return filled(edit.body) || filled(edit.footer) || (edit.buttons || []).some(filled);
};

/** Content fingerprint of one language, to compare with what was submitted. */
export const contentHash = (draft: TemplateDraft, lang: AutoReplyLang) =>
  JSON.stringify([
    draft.body[lang], draft.footer?.[lang] || '',
    (draft.buttons || []).map((b) => b[lang]),
    ...(draft.urlButton ? [draft.urlButton.template] : []),
  ]);

/**
 * Template approval status per name + language (Meta's live list first, then
 * our submission records), the saved edits, and versioned submit.
 */
export const useTemplateStatuses = () => {
  const { currentUser } = useAuth();
  const [statuses, setStatuses] = useState<Record<string, TemplateStatus>>({});
  const [metaTemplates, setMetaTemplates] = useState<MetaTemplateRow[]>([]);
  const [edits, setEdits] = useState<Record<string, TemplateEditDoc>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<string | null>(null);

  useEffect(
    () =>
      onSnapshot(
        collection(db, EDITS_COLLECTION),
        (snap) => {
          const next: Record<string, TemplateEditDoc> = {};
          snap.docs.forEach((d) => {
            next[d.id] = d.data() as TemplateEditDoc;
          });
          setEdits(next);
        },
        () => setEdits({})
      ),
    []
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const next: Record<string, TemplateStatus> = {};
    try {
      const subs = await getDocs(collection(db, 'whatsappTemplateSubmissions'));
      subs.docs.forEach((d) => {
        const data = d.data() as any;
        next[templateKey(data.name, data.language)] = {
          status: String(data.status || ''),
          category: data.metaCategory || data.category,
          error: data.error || null,
        };
      });
    } catch {
      // Submissions are optional; Meta's list below is the source of truth.
    }
    try {
      const res: any = await httpsCallable(functions, 'whatsappFetchTemplates')({});
      const rows: MetaTemplateRow[] = (res?.data?.templates || []).map((t: any) => ({
        id: String(t.id || ''),
        name: String(t.name || ''),
        language: String(t.language || ''),
        status: String(t.status || ''),
        category: String(t.category || ''),
      }));
      rows.forEach((t) => {
        next[templateKey(t.name, t.language)] = { status: t.status, category: t.category, error: null };
      });
      setMetaTemplates(rows);
    } catch (err: any) {
      setError(`Could not load templates from Meta: ${err?.message || err}`);
    }
    setStatuses(next);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const effective = useCallback(
    (base: string) => {
      const draft = TEMPLATE_DRAFTS.find((d) => d.name === base);
      return draft ? applyEdits(draft, edits[base]) : undefined;
    },
    [edits]
  );

  /** Versions for one language: what automations send now, and what is in review. */
  const versionsFor = useCallback(
    (base: string, lang: AutoReplyLang) => {
      const names = edits[base]?.names?.[lang] || {};
      const code = TEMPLATE_LANG_CODES[lang];
      const live = String(names.live || base);
      const next = names.next ? String(names.next) : null;
      const liveStatus = statuses[templateKey(live, code)];
      const nextStatus = next ? statuses[templateKey(next, code)] : undefined;
      // A newer approved version takes over automatically (same rule as the backend).
      const sending = nextStatus?.status === 'APPROVED' ? next : liveStatus?.status === 'APPROVED' ? live : null;
      return { live, liveStatus, next, nextStatus, sending };
    },
    [edits, statuses]
  );

  /** Status shown for a base template + language. */
  const statusFor = useCallback(
    (base: string, lang: AutoReplyLang): TemplateStatus | undefined => {
      const v = versionsFor(base, lang);
      if (v.sending) return { status: 'APPROVED' };
      return v.nextStatus || v.liveStatus;
    },
    [versionsFor]
  );

  /** Edited (saved) since this language was last submitted. */
  const isChanged = useCallback(
    (base: string, lang: AutoReplyLang) => {
      const draft = effective(base);
      const submitted = edits[base]?.submitted?.[lang];
      if (!draft) return false;
      if (!submitted) return !!draft.urlButton || hasEdits(edits[base]);
      return submitted.hash !== contentHash(draft, lang);
    },
    [edits, effective]
  );

  const saveEdits = useCallback(
    async (base: string, patch: Pick<TemplateEditDoc, 'body' | 'footer' | 'buttons'>) => {
      const draftRevision = TEMPLATE_DRAFTS.find((draft) => draft.name === base)?.draftRevision || 0;
      await setDoc(
        doc(db, EDITS_COLLECTION, base),
        { ...patch, draftRevision, updatedAt: serverTimestamp(), updatedByEmail: currentUser?.email || null },
        { merge: true }
      );
    },
    [currentUser?.email]
  );

  /** Back to the built-in text. Versions and submit history are kept. */
  const resetEdits = useCallback(
    async (base: string) => {
      const draftRevision = TEMPLATE_DRAFTS.find((draft) => draft.name === base)?.draftRevision || 0;
      await setDoc(
        doc(db, EDITS_COLLECTION, base),
        { body: deleteField(), footer: deleteField(), buttons: deleteField(), draftRevision, updatedAt: serverTimestamp(), updatedByEmail: currentUser?.email || null },
        { merge: true }
      );
    },
    [currentUser?.email]
  );

  /**
   * Submit one language. Never touches an approved version in use: changes to
   * an approved template go out as a new version (base_v2, ...) that takes
   * over once Meta approves it. Rejected versions are fixed in place.
   */
  const submit = useCallback(
    async (draftIn: TemplateDraft, lang: AutoReplyLang): Promise<string | null> => {
      const base = draftIn.name;
      // Submit exactly what the caller passes (the editor may have just saved it).
      const draft = draftIn;
      const code = TEMPLATE_LANG_CODES[lang];
      const edit = edits[base] || {};
      const v = versionsFor(base, lang);
      const idOf = (name: string) => metaTemplates.find((t) => t.name === name && t.language === code)?.id || '';
      const hash = contentHash(draft, lang);
      let target = v.live;
      let templateId = '';
      let namesPatch: { live?: string | null; next?: string | null } = {};
      let versionCounter = Number(edit.versionCounter || 1);

      const liveState = v.liveStatus?.status || '';
      const nextState = v.nextStatus?.status || '';
      if (v.next && IN_REVIEW.has(nextState)) return `${v.next} is in review with Meta. Wait for the result before submitting again.`;
      if (v.next && nextState === 'APPROVED') {
        // Promote the approved version, then treat it as the live one.
        namesPatch = { live: v.next, next: null };
      }
      const liveName = namesPatch.live || v.live;
      const liveNowState = namesPatch.live ? 'APPROVED' : liveState;

      if (v.next && !namesPatch.live) {
        // Fix or resubmit the version in progress.
        target = v.next;
        templateId = FIXABLE.has(nextState) ? idOf(v.next) : '';
      } else if (!liveNowState || liveNowState === 'SUBMIT_FAILED') {
        target = liveName;
      } else if (FIXABLE.has(liveNowState)) {
        target = liveName;
        templateId = idOf(liveName);
      } else if (IN_REVIEW.has(liveNowState)) {
        return `${liveName} is in review with Meta. Wait for the result before submitting again.`;
      } else {
        // Approved (or paused/disabled) and in use: send changes as a new version.
        if (edit.submitted?.[lang]?.hash === hash && edit.submitted?.[lang]?.name === liveName) {
          return 'No changes since this version was submitted.';
        }
        versionCounter += 1;
        target = `${base}_v${versionCounter}`;
        namesPatch = { ...namesPatch, live: liveName, next: target };
      }

      const key = templateKey(target, code);
      setSubmitting(templateKey(base, code));
      try {
        const res: any = await httpsCallable(functions, 'whatsappSubmitTemplate')({
          name: target,
          language: code,
          category: draft.category,
          components: templateComponents(draft, lang),
          bodyText: draft.body[lang],
          ...(draft.header?.type === 'DOCUMENT' ? { referenceTemplate: draft.header.referenceTemplate } : {}),
          ...(templateId ? { templateId } : {}),
        });
        setStatuses((prev) => ({
          ...prev,
          [key]: { status: String(res?.data?.status || 'PENDING'), category: res?.data?.category, error: null },
        }));
        await setDoc(
          doc(db, EDITS_COLLECTION, base),
          {
            names: { [lang]: { live: namesPatch.live ?? liveName, ...(namesPatch.next !== undefined ? { next: namesPatch.next } : {}) } },
            versionCounter,
            submitted: { [lang]: { name: target, hash } },
            updatedAt: serverTimestamp(),
            updatedByEmail: currentUser?.email || null,
          },
          { merge: true }
        );
        return null;
      } catch (err: any) {
        const message = String(err?.message || err);
        setStatuses((prev) => ({ ...prev, [key]: { status: 'SUBMIT_FAILED', error: message } }));
        return message;
      } finally {
        setSubmitting(null);
      }
    },
    [currentUser?.email, edits, metaTemplates, versionsFor]
  );

  /** Languages that need submitting: never sent, failed, rejected, or edited since. */
  const needsSubmit = useCallback(
    (base: string, lang: AutoReplyLang) => {
      const v = versionsFor(base, lang);
      const state = (v.next ? v.nextStatus : v.liveStatus)?.status || '';
      if (IN_REVIEW.has(state)) return false;
      if (!state || state === 'SUBMIT_FAILED' || FIXABLE.has(state)) return true;
      return isChanged(base, lang);
    },
    [isChanged, versionsFor]
  );

  const langs = useMemo(() => AUTO_REPLY_LANGS.map((l) => l.key), []);

  return {
    statuses,
    metaTemplates,
    edits,
    loading,
    error,
    submitting,
    refresh,
    submit,
    effective,
    versionsFor,
    statusFor,
    isChanged,
    needsSubmit,
    saveEdits,
    resetEdits,
    langs,
  };
};

export type TemplateStatusesApi = ReturnType<typeof useTemplateStatuses>;

const STATUS_STYLE: Record<string, { label: string; bg: string; fg: string; border?: string }> = {
  APPROVED: { label: 'Approved', bg: '#edf6ee', fg: '#2f6b35' },
  PENDING: { label: 'In review', bg: '#fbf3e6', fg: '#8a5a12' },
  IN_APPEAL: { label: 'In appeal', bg: '#fbf3e6', fg: '#8a5a12' },
  REJECTED: { label: 'Rejected', bg: '#fbeceb', fg: '#a12a22' },
  SUBMIT_FAILED: { label: 'Failed', bg: '#fbeceb', fg: '#a12a22' },
  PAUSED: { label: 'Paused', bg: '#f0f0ee', fg: '#4f4c47' },
  DISABLED: { label: 'Disabled', bg: '#f0f0ee', fg: '#4f4c47' },
};

export const isApproved = (s?: TemplateStatus) => s?.status === 'APPROVED';

export const TemplateStatusChip = ({ lang, status }: { lang: string; status?: TemplateStatus }) => {
  const style = status ? STATUS_STYLE[status.status] : null;
  const chip = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize: 11.5,
        lineHeight: 1,
        px: 0.75,
        py: 0.5,
        borderRadius: 1,
        whiteSpace: 'nowrap',
        bgcolor: style?.bg || 'transparent',
        color: style?.fg || UI.muted,
        border: style ? 'none' : `1px dashed ${UI.line}`,
      }}
    >
      <Box component="span" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 10.5 }}>{lang}</Box>
      {style?.label || status?.status || 'Not submitted'}
    </Box>
  );
  return status?.error ? <Tooltip title={status.error}>{chip}</Tooltip> : chip;
};

/** "2/3 approved" style summary for a set of base template names. */
export type TemplateIssueState = 'missing' | 'review' | 'rejected' | 'failed' | 'inactive';

const ISSUE_TEXT: Record<TemplateIssueState, string> = {
  missing: 'not submitted to Meta',
  review: 'in review with Meta',
  rejected: 'rejected by Meta',
  failed: 'submit failed',
  inactive: 'paused or disabled by Meta',
};

/**
 * Why a journey's templates cannot send yet, one line per template, e.g.
 * "swarna_enquiry_nudge: not submitted to Meta (en, gu, hi)". English is the
 * fallback language, so a template without an approved English version
 * blocks sending; a missing Gujarati / Hindi only falls back to English.
 * Empty while statuses are loading or Meta could not be reached.
 */
export const templateIssues = (names: string[], api: TemplateStatusesApi) => {
  const lines: Array<{ name: string; text: string; blocking: boolean }> = [];
  if (api.loading || api.error) return lines;
  names.forEach((name) => {
    const byState = new Map<TemplateIssueState, AutoReplyLang[]>();
    templateLanguages(name, api).forEach((lang) => {
      const s = api.statusFor(name, lang);
      if (isApproved(s)) return;
      const st = s?.status || '';
      const state: TemplateIssueState = !st ? 'missing' : IN_REVIEW.has(st) ? 'review' :
        FIXABLE.has(st) ? 'rejected' : st === 'SUBMIT_FAILED' ? 'failed' : 'inactive';
      byState.set(state, [...(byState.get(state) || []), lang]);
    });
    const blocking = !isApproved(api.statusFor(name, 'en'));
    byState.forEach((langs, state) => {
      lines.push({
        name,
        blocking,
        text: `${name}: ${ISSUE_TEXT[state]} (${langs.join(', ')})${blocking ? '' : ', sends in English instead'}`,
      });
    });
  });
  return lines;
};

export const approvalSummary = (names: string[], api: TemplateStatusesApi) => {
  const total = names.reduce((sum, name) => sum + templateLanguages(name, api).length, 0);
  const approved = names.reduce((sum, name) => sum + templateLanguages(name, api).filter((l) => isApproved(api.statusFor(name, l))).length, 0);
  return { approved, total };
};

const templateLanguages = (name: string, api: TemplateStatusesApi): AutoReplyLang[] =>
  api.langs;

export const CategoryTag = ({ category }: { category: string }) => (
  <Box
    component="span"
    sx={{
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: 0.5,
      color: category === 'MARKETING' ? '#7a3e9d' : '#2f5f8a',
      bgcolor: category === 'MARKETING' ? '#f4eef8' : '#eef3f8',
      borderRadius: 1,
      px: 0.6,
      py: 0.2,
    }}
  >
    {category === 'MARKETING' ? 'MARKETING' : 'UTILITY'}
  </Box>
);

export const WhatsappPhonePreview = ({
  header,
  body,
  footer,
  buttons,
  buttonUrls,
}: {
  header?: React.ReactNode;
  body: string;
  footer?: string;
  buttons?: string[];
  buttonUrls?: Array<string | undefined>;
}) => (
  <Box sx={{ bgcolor: '#efe7dd', borderRadius: 2, p: 1.5 }}>
    <Box sx={{ maxWidth: 300, ml: 'auto' }}>
      <Box sx={{ bgcolor: '#fdfdfc', borderRadius: 1.75, px: 1.25, pt: 1, pb: 0.75, boxShadow: '0 1px 1px rgba(40,30,20,0.08)' }}>
        {header}
        <Box sx={{ fontSize: 13, color: '#111b21', whiteSpace: 'pre-wrap', lineHeight: 1.45, wordBreak: 'break-word' }}>{renderWhatsappText(body)}</Box>
        {footer && <Box sx={{ fontSize: 11.5, color: '#667781', mt: 0.5 }}>{footer}</Box>}
      </Box>
      {(buttons || []).map((label, index) => (
        <Box key={`${label}-${index}`} component={buttonUrls?.[index] ? 'a' : 'div'}
          href={buttonUrls?.[index]} target={buttonUrls?.[index] ? '_blank' : undefined}
          rel={buttonUrls?.[index] ? 'noopener noreferrer' : undefined}
          sx={{ display: 'block', textDecoration: 'none', bgcolor: '#fdfdfc', borderRadius: 1.75, mt: 0.4, py: 0.7, textAlign: 'center', fontSize: 13, color: '#027eb5', boxShadow: '0 1px 1px rgba(40,30,20,0.08)' }}>
          {label}
        </Box>
      ))}
    </Box>
  </Box>
);

/** Show WhatsApp formatting in previews while editors retain the literal source text. */
export const renderWhatsappText = (value: string): React.ReactNode =>
  value.split(/(```[\s\S]+?```|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g).map((part, index) => {
    if (part.startsWith('```') && part.endsWith('```')) return <code key={index} style={{ fontFamily: UI.mono }}>{part.slice(3, -3)}</code>;
    if (/^\*[^*\n]+\*$/.test(part)) return <strong key={index}>{part.slice(1, -1)}</strong>;
    if (/^_[^_\n]+_$/.test(part)) return <em key={index}>{part.slice(1, -1)}</em>;
    if (/^~[^~\n]+~$/.test(part)) return <s key={index}>{part.slice(1, -1)}</s>;
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
